'use client'

import {
	getProductDocumentsBySerialNumber,
	getProductDocumentsByPartNumber,
} from '@/lib/digital-twin'
import SearchInput from '@desoutter/uikit/src/components/atoms/SearchInput'
import { useParams, useRouter } from 'next/navigation'
import { FormEvent, useCallback, useMemo, useState } from 'react'
import { DigitalTwinSearchTranslationsFragment } from './DigitalTwinSearch.generated'
type DigitalTwinSearchProps = {
	label: string
	placeholder?: string
	translations: DigitalTwinSearchTranslationsFragment
}

export function DigitalTwinSearch({
	label,
	placeholder,
	translations,
}: DigitalTwinSearchProps) {
	const [inputValue, setInputValue] = useState('')
	const [error, setError] = useState('')
	const [isLoading, setIsLoading] = useState(false)
	const [searchType, setSearchType] = useState<'partNumber' | 'serialNumber'>(
		'partNumber',
	)
	const router = useRouter()
	const { lang } = useParams()

	const formattedError = useMemo(() => {
		if (!error) return ''
		return error.replace(
			/<a href="([^"]+)">([^<]+)<\/a>/g,
			'<a href="$1" class="underline text-blue-600 hover:text-blue-800 focus:outline focus:outline-2 focus:outline-blue-300">$2</a>',
		)
	}, [error])

	const searchByPartNumber = useCallback(
		async (e: FormEvent<HTMLFormElement>) => {
			e.preventDefault()

			if (!inputValue.length) return

			const data = await getProductDocumentsByPartNumber(
				inputValue,
				lang as string,
			)

			return router.push(
				`/${lang ?? 'en_US'}/products/${data.partNumber}#spy_${translations.resources}`,
			)
		},
		[inputValue, lang, router],
	)

	const searchBySerialNumber = useCallback(
		async (e: FormEvent<HTMLFormElement>) => {
			e.preventDefault()

			if (!inputValue.length) return

			const data = await getProductDocumentsBySerialNumber(
				inputValue,
				lang as string,
			)

			return router.push(
				`/${lang ?? 'en_US'}/digital-twin/${data.partNumber}?sn=${data.serialNumber}`,
			)
		},
		[inputValue, lang, router],
	)

	const handleSubmit = useCallback(
		async (e: FormEvent<HTMLFormElement>) => {
			e.preventDefault()
			setIsLoading(true)
			setError(null)

			if (!inputValue.length) {
				setIsLoading(false)
				return
			}

			try {
				if (searchType === 'partNumber') {
					await searchByPartNumber(e)
				} else {
					await searchBySerialNumber(e)
				}
				setIsLoading(false)
			} catch (error) {
				setError(error)
				setIsLoading(false)
			}
		},
		[searchByPartNumber, searchBySerialNumber, searchType, inputValue],
	)

	const handleSearchTypeChange = (type: 'partNumber' | 'serialNumber') => {
		setSearchType(type)
		setError('')
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			<div className="flex items-center space-x-4 mb-2">
				<div className="flex items-center">
					<input
						type="radio"
						id="partNumber"
						name="searchType"
						value="partNumber"
						checked={searchType === 'partNumber'}
						onChange={() => handleSearchTypeChange('partNumber')}
						className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
					/>
					<label
						htmlFor="partNumber"
						className="ml-2 block text-sm font-medium"
					>
						{translations?.partNumber}
					</label>
				</div>
				<div className="flex items-center">
					<input
						type="radio"
						id="serialNumber"
						name="searchType"
						value="serialNumber"
						checked={searchType === 'serialNumber'}
						onChange={() => handleSearchTypeChange('serialNumber')}
						className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
					/>
					<label
						htmlFor="serialNumber"
						className="ml-2 block text-sm font-medium"
					>
						{translations?.serialNumber}
					</label>
				</div>
			</div>
			<SearchInput
				label={label}
				placeholder={placeholder}
				setSearch={setInputValue}
				isLoading={isLoading}
			/>
			<span
				className="text-primary text-xs mt-2"
				dangerouslySetInnerHTML={{ __html: formattedError }}
			/>
		</form>
	)
}
